import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import Caption from 'LuxKit/Typography/Caption'
import * as Analytics from 'analytics/analytics'
import { propertyRateImpressionEventWithContext } from 'analytics/snowplow/events'
import AccommodationPackageInclusionsList from 'components/AccommodationKit/AccommodationPackageInclusionsList'
import AccommodationPackageTile from 'components/AccommodationKit/AccommodationPackageTile'
import AccommodationPackageTileContentArea from 'components/AccommodationKit/AccommodationPackageTileContentArea'
import AccommodationPackageTileDetails from 'components/AccommodationKit/AccommodationPackageTileDetails'
import AccommodationPackageTilePricingArea from 'components/AccommodationKit/AccommodationPackageTilePricingArea'
import AccommodationPackageTilePricingBlock from 'components/AccommodationKit/AccommodationPackageTilePricingBlock'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import ModalContext from 'contexts/ModalContext'
import { OfferPageActions, OfferPageRoomOption } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useBedbankDiscount from 'hooks/useBedbankDiscount'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { selectLoggedIn } from 'selectors/accountSelectors'
import PropertyAvailableRateOptionCtas from './PropertyAvailableRateOptionCtas'
import PropertyCancellationPolicy from './PropertyCancellationPolicy'
import PropertyUnavailableRateOptionCtas from './PropertyUnavailableRateOptionCtas'
import BusinessTravellerItemPolicyOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerItemPolicyOfferCreditsTextLink'
import { isMultiCartEnabled } from 'selectors/featuresSelectors'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import { getSource } from 'selectors/offerSelectors'
import { OfferAndRate, propertySingleRoomBookNowOrAddToCartCallback } from './PropertyBuyCallback'
import { getPropertyOfferMapping } from 'selectors/offerPage/offerPageSelectors'
import { SOURCE_TYPES } from 'constants/source'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { allocateTravellersToRooms } from 'businessTraveller/utils/allocateTravellersToRooms'
import { useMultiRoomBookingGuard } from 'businessTraveller/hooks/useMultiRoomBookingGuard'
import HotelLoyaltyPointsEarnLabel from '../shared/HotelLoyaltyPointsEarnLabel'
import { filteredInclusionsWithLoyaltyEligibility } from '../utils'
import { getHotelLoyaltyProgramNameFromRateInclusions } from 'lib/hotels/hotelLoyaltyEligibility'
import { selectIsLebtSabreHotelsEnabled } from 'businessTraveller/selectors/selectIsLebtSabreHotelsEnabled'
import { getPropertyRateDisplayName } from 'lib/property/getPropertyRateDisplayName'

interface Props {
  propertyId: string;
  lpcOffer?: App.HotelOffer;
  bedbankOffer: App.BedbankOffer | undefined;
  rate: App.PropertyRate;
  optionNumber: number; // TODO one-indexed
  originAirport: App.Airport | undefined;
  checkIn: string | undefined;
  checkOut: string | undefined;
  duration: number;
  rooms: Array<App.Occupants>;
  regionCode: string;
  currency: string;
  source: string;
  utmContent: string | undefined;
  roomOption: OfferPageRoomOption | undefined;
  isLoggedIn: boolean;
  isSpoofed: boolean;
  roomListId: string;
  roomPosition: number; // TODO one-indexed
  rateListId: string;
  userMultiItemCartEnabled: boolean;
  selectedTravellerEmployeeIds: Array<string> | undefined;
  selectedTravellerGuestIds?: Array<string> | undefined;
  loyaltySelectorContentOverride?: React.ReactNode;
}

function PropertyRateOption(props: Props) {
  const {
    propertyId,
    lpcOffer,
    bedbankOffer,
    rate,
    optionNumber,
    originAirport,
    checkIn,
    checkOut,
    duration,
    rooms,
    regionCode,
    currency,
    source,
    utmContent,
    roomOption,
    isLoggedIn,
    isSpoofed,
    roomListId,
    roomPosition,
    rateListId,
    userMultiItemCartEnabled,
    selectedTravellerEmployeeIds,
    selectedTravellerGuestIds,
    loyaltySelectorContentOverride,
  } = props

  const dispatch = useAppDispatch()
  const offerPageDispatch = useOfferPageDispatch()
  const modalElementContext = useModalElementContext()
  const showModal = useContext(ModalContext)
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const [ref, inView] = useInView({ triggerOnce: true })

  // TODO maybe this should just use bedbank as a fallback?
  const propertyTimeZone = (rate.type === 'bedbank' && bedbankOffer) ? bedbankOffer.property.timezone : lpcOffer?.property.timezone ?? ''
  // TODO `formattedShortDateByTimezone` includes a hack for string timestamps suffixed with `'T00:00:00.000Z'`, which we want to avoid
  const headlineCancellationPolicyStart = rate.headlineCancellationPolicyStart ? new Date(rate.headlineCancellationPolicyStart) : undefined
  const rateDisplayName = getPropertyRateDisplayName(rate)
  const tileDetailsProps = { rateName: rateDisplayName }
  const rawBedbankDiscount = useBedbankDiscount(rate.total.discount, rate.luxPlusTotal?.discount ?? 0)

  useEffect(() => {
    const offer = rate.type === 'lpc' ? lpcOffer : bedbankOffer
    if (inView && offer && rooms && checkIn && checkOut) {
      Analytics.trackEvent(propertyRateImpressionEventWithContext({
        offer,
        occupancy: rooms,
        checkIn,
        checkOut,
        roomListId,
        roomPosition,
        rateListId,
        ratePosition: optionNumber,
        rate,
        currency,
      }))
    }
  }, [
    bedbankOffer,
    checkIn,
    checkOut,
    currency,
    inView,
    lpcOffer,
    optionNumber,
    rate,
    rateListId,
    roomListId,
    roomPosition,
    rooms,
  ])

  const guardBookingFlow = useMultiRoomBookingGuard()

  const onCtaClick = useCallback(async(shouldAddToCart: boolean) => {
    if (guardBookingFlow()) return
    if (!rooms) return
    if (!(checkIn && checkOut)) throw new Error('Attempted to perform cart action when dates aren\'t set')
    trackEvent('BOOK_NOW_PRESSED')

    // TODO we don't care about walled garden specifics
    if (!isLoggedIn) {
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (!result.loggedIn) {
        return
      }
    }

    let offerAndRate: OfferAndRate

    if (rate.type === 'lpc') {
      if (!lpcOffer) return
      offerAndRate = { type: 'lpc', offer: lpcOffer, rate }
    } else if (rate.type === 'bedbank') {
      if (!bedbankOffer) return
      offerAndRate = { type: 'bedbank', offer: bedbankOffer, rate }
    } else {
      // Assert that `offerAndRate` must be assigned by this point.
      assertUnreachable(rate)
    }

    // Allocate travellers to rooms
    const roomsWithTravellerInfo = allocateTravellersToRooms(
      rooms,
      selectedTravellerEmployeeIds,
      selectedTravellerGuestIds,
    )

    propertySingleRoomBookNowOrAddToCartCallback({
      shouldAddToCart,
      dispatch,
      offerPageDispatch,
      modalElementContext,
      showModal,
      propertyId,
      offerAndRate,
      optionNumber,
      originAirportCode: originAirport?.code,
      checkIn,
      checkOut,
      duration,
      rooms: roomsWithTravellerInfo,
      regionCode,
      currency,
      source: source ? `${source},${SOURCE_TYPES.CTA}` : SOURCE_TYPES.CTA,
      utmContent,
      roomOption,
      isSpoofed,
      roomListId,
      roomPosition,
      rateListId,
    })
  }, [guardBookingFlow, bedbankOffer, checkIn, checkOut, currency, dispatch, duration, isLoggedIn, isSpoofed, lpcOffer, modalElementContext, offerPageDispatch, optionNumber, originAirport?.code, propertyId, rate, rateListId, regionCode, roomListId, roomOption, roomPosition, rooms, selectedTravellerEmployeeIds, selectedTravellerGuestIds, showModal, source, trackEvent, utmContent])

  const isLoyaltyProgramFeatureFlagEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.lebt_hotel_loyalty)
  const sabreHotelsEnabled = useAppSelector(selectIsLebtSabreHotelsEnabled)

  const onToggleFilter = useCallback((filter: 'airport' | 'checkin') => {
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch])

  const ctas = <>
    {(!rate.includesFlights || typeof rate.cheapestFlightPrice === 'number') && <PropertyAvailableRateOptionCtas
      onCtaClick={onCtaClick}
      showAddToCart={userMultiItemCartEnabled}
    />}
    {rate.includesFlights && typeof rate.cheapestFlightPrice === 'string' && <PropertyUnavailableRateOptionCtas
      shouldChangeAirport={rate.cheapestFlightPrice === 'originSameAsDestination' || rate.cheapestFlightPrice === 'unavailable'}
      shouldChangeDepartureDate={rate.cheapestFlightPrice === 'departureTooEarly' || rate.cheapestFlightPrice === 'departureTooLate'}
      onToggleFilter={onToggleFilter}
    />}
  </>

  const cancellationPolicy = loyaltySelectorContentOverride ?? <PropertyCancellationPolicy
    propertyTimeZone={propertyTimeZone}
    headlineCancellationPolicyType={rate.headlineCancellationPolicyType}
    headlineCancellationPolicyStart={headlineCancellationPolicyStart}
    fullyRefundableUntil={rate.fullyRefundableUntil}
    cancellationPolicies={rate.cancellationPolicies}
    currency={currency}
    shouldClarifyRefundIsHotelOnly={rate.includesFlights}
  />

  const { filteredInclusions, isEligibleForLoyaltyProgram } = useMemo(
    () => filteredInclusionsWithLoyaltyEligibility(
      rate.inclusions,
      {
        isHotelLoyaltyEnabled: isLoyaltyProgramFeatureFlagEnabled,
        sabreHotelsEnabled,
      },
      rate.type === 'bedbank' ? rate.bedbankSupplierCode : undefined,
    ),
    [rate, isLoyaltyProgramFeatureFlagEnabled, sabreHotelsEnabled]
  )

  const loyaltyProgramName = useMemo(
    () => getHotelLoyaltyProgramNameFromRateInclusions(rate.inclusions),
    [rate.inclusions],
  )

  const loyaltyPointsEarnLabel = isEligibleForLoyaltyProgram && !loyaltySelectorContentOverride
    ? <HotelLoyaltyPointsEarnLabel loyaltyProgramName={loyaltyProgramName} />
    : undefined

  if (rate.type === 'lpc') {
    if (!lpcOffer) return null
    const lpcDiscount = rate.total.discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? rate.total.discount : 0
    const shouldShowValue = rate.total.valuedUpToPrice > rate.total.sellingPrice
    const showPriceDetails = !!(rate.total.taxesAndFees || rate.total.propertyFees)

    return (
      <AccommodationPackageTile>
        <AccommodationPackageTileContentArea>
          <AccommodationPackageTileDetails
            {...tileDetailsProps}
          >
            <Group direction="vertical" gap={8} noDisplayWhenEmpty>
              <Group direction="vertical" gap={4} noDisplayWhenEmpty>
                {cancellationPolicy}
              </Group>
            </Group>
          </AccommodationPackageTileDetails>
          <div>
            <LargeContentToggle
              maxHeight={208}
              showMoreText="View more inclusions"
              showLessText="View less inclusions"
            >
              <VerticalSpacer gap={12}>
                {rate.luxPlusInclusions.length > 0 && <LuxPlusInclusions
                  luxPlusInclusions={rate.luxPlusInclusions}
                  accommodationOffer={lpcOffer}
                />}
                {rate.bonusInclusions.length > 0 && <AccommodationPackageInclusionsList title="bonus">
                  {rate.bonusInclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
                </AccommodationPackageInclusionsList>}
                {rate.inclusions.length > 0 && <VerticalSpacer gap={4}>
                  {rate.inclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
                </VerticalSpacer>}
              </VerticalSpacer>
            </LargeContentToggle>
          </div>
        </AccommodationPackageTileContentArea>
        <AccommodationPackageTilePricingArea>
          {(!rate.includesFlights || (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0)) && <Group direction="horizontal" gap={4} wrap="wrap">
            {lpcDiscount > 0 && <PriceRowValueDiscount
              type="estimate"
              discountPercentage={lpcDiscount}
            />}
          </Group>}
          <AccommodationPackageTilePricingBlocks>
            <AccommodationPackageTilePricingBlock
              title={undefined}
              callToAction={ctas}
            >
              {rate.includesFlights && rate.cheapestFlightPrice === 'loading' && <LoadingIndicator floating opaque />}
              {(!rate.includesFlights || (typeof rate.cheapestFlightPrice === 'number' && rate.cheapestFlightPrice > 0)) && <VerticalSpacer gap={12}>
                <div>
                  <Caption variant="medium">
                    <Group direction="vertical" gap={4}>
                      <div>
                        <PriceRowPriceCaption>
                          {rate.includesFlights && <LinePlaneDepartureIcon size="S" />} {pluralizeToString('night', duration)} {rate.includesFlights && '+ flights from'}
                        </PriceRowPriceCaption>
                        <LuxPlusPriceGuard
                          showMemberPrice={(rate.luxPlusTotal?.displayPrice ?? 0) > 0}
                          fallbackPrice={<PriceRowPrice
                            size="M"
                            price={rate.total.displayPrice}
                            saleUnit="total"
                          />}
                        >
                          <LuxPlusPriceStack
                            size="M"
                            price={rate.total.displayPrice}
                            memberPrice={rate.luxPlusTotal?.displayPrice ?? 0}
                            saleUnit="total"
                          />
                        </LuxPlusPriceGuard>
                        <div ref={ref}></div>
                        <PriceRowTaxesAndFees />
                        {shouldShowValue && <PriceRowValueDiscountWithCaption
                          size="M"
                          originalValue={rate.total.valuedUpToPrice}
                        />}
                        <BusinessTravellerItemPolicyOfferCreditsTextLink
                          type="exact"
                          offer={lpcOffer}
                          totalCost={rate.total.displayPrice}
                          numberOfNights={duration}
                          roomCount={rooms?.length ?? 1}
                        />
                      </div>
                      {rate.includedGuestsExceeded && <AdditionalGuestsPopup
                        complex={false}
                        description="+ Extra guest surcharge"
                        modalContent={rate.includedGuestsExceededPolicy}
                      />}
                      {/* NB: Don't pass `flightPrice` to `<OfferPriceDetailsRow/>`, as it's already baked in. */}
                      {showPriceDetails && <OfferPriceDetailsRow
                        trigger="price-row"
                        triggerSize="M"
                        offerPage
                        offer={lpcOffer}
                        duration={duration}
                        propertyFees={rate.total.propertyFees}
                        hotelPrice={rate.total.sellingPrice}
                        taxesAndFees={rate.total.taxesAndFees}
                        hotelMemberPrice={rate.luxPlusTotal?.sellingPrice}
                        showMemberPrice={(rate.luxPlusTotal?.displayPrice ?? 0) > 0}
                        rooms={rooms}
                        dueAtPropertyMessage={lpcOffer.property.taxesAndFeesContent ?? ''}
                        cta={<Group direction="vertical" tabletDirection="horizontal-reverse" horizontalAlign="stretch" tabletHorizontalAlign="end" gap={8}>
                          {ctas}
                        </Group>}
                      />}
                    </Group>
                  </Caption>
                </div>
              </VerticalSpacer>}
            </AccommodationPackageTilePricingBlock>
          </AccommodationPackageTilePricingBlocks>
        </AccommodationPackageTilePricingArea>
      </AccommodationPackageTile>
    )
  }

  if (rate.type === 'bedbank' && bedbankOffer) {
    const bedbankDiscount = rawBedbankDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? rawBedbankDiscount : 0
    // TODO note we'll completely ignore `bedbankOffer.displayPricingAsPerPerson`

    return (
      <AccommodationPackageTile>
        <AccommodationPackageTileContentArea>
          <AccommodationPackageTileDetails
            {...tileDetailsProps}
            tagLineLabel={<>
              {rate.corporate && <ProductTypeLabel productType="corporate" />}
            </>}
          >
            <Group direction="vertical" gap={8} noDisplayWhenEmpty>
              {cancellationPolicy}
              {loyaltyPointsEarnLabel}
            </Group>
          </AccommodationPackageTileDetails>
          <div>
            {/* TODO !!rate.promotions.length && <div></div> */}
            {filteredInclusions.length > 0 && <VerticalSpacer gap={4}>
              {filteredInclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
            </VerticalSpacer>}
          </div>
        </AccommodationPackageTileContentArea>
        <AccommodationPackageTilePricingArea>
          {bedbankDiscount > 0 && <PriceRowValueDiscount
            type="estimate"
            discountPercentage={bedbankDiscount}
          />}
          <AccommodationPackageTilePricingBlocks>
            <AccommodationPackageTilePricingBlock
              title={undefined}
              callToAction={ctas}
            >
              <VerticalSpacer gap={12}>
                <div>
                  <LuxPlusPriceGuard
                    showMemberPrice={(rate.luxPlusTotal?.displayPrice ?? 0) > 0}
                    fallbackPrice={<PriceRowPrice
                      size="M"
                      price={rate.total.displayPrice}
                      saleUnit="total"
                    />}
                  >
                    <LuxPlusPriceStack
                      size="M"
                      price={rate.total.displayPrice}
                      memberPrice={rate.luxPlusTotal?.displayPrice ?? 0}
                      saleUnit="total"
                    />
                  </LuxPlusPriceGuard>
                  <div ref={ref}></div>
                  <PriceRowTaxesAndFees />
                  {!!rawBedbankDiscount && <PriceRowValueDiscountWithCaption
                    size="M"
                    originalValue={rate.total.valuedUpToPrice}
                  />}
                  <BusinessTravellerItemPolicyOfferCreditsTextLink
                    type="exact"
                    offer={bedbankOffer}
                    totalCost={rate.total.displayPrice}
                    numberOfNights={duration}
                    roomCount={rooms?.length ?? 1}
                  />
                  {/* NB: Don't pass `flightPrice` to `<OfferPriceDetailsRow/>`, as it's already baked in. */}
                  <OfferPriceDetailsRow
                    trigger="price-row"
                    triggerSize="M"
                    offerPage
                    offer={bedbankOffer}
                    rooms={rooms}
                    propertyFees={rate.total.propertyFees}
                    taxesAndFees={rate.total.taxesAndFees}
                    hotelPrice={rate.total.sellingPrice}
                    hotelMemberPrice={rate.luxPlusTotal?.sellingPrice}
                    showMemberPrice={(rate.luxPlusTotal?.displayPrice ?? 0) > 0}
                    duration={duration}
                    cta={<Group direction="vertical" tabletDirection="horizontal-reverse" tabletHorizontalAlign="end" gap={8}>
                      {ctas}
                    </Group>}
                  />
                </div>
              </VerticalSpacer>
            </AccommodationPackageTilePricingBlock>
          </AccommodationPackageTilePricingBlocks>
        </AccommodationPackageTilePricingArea>
      </AccommodationPackageTile>
    )
  }
}

export default withOfferPageAndReduxState((state, appState) => ({
  propertyId: getPropertyOfferMapping(state, appState)!.propertyId,
  originAirport: state.airport,
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  duration: state.duration,
  rooms: state.rooms,
  regionCode: appState.geo.currentRegionCode,
  currency: appState.geo.currentCurrency,
  source: getSource(appState),
  utmContent: appState.router.location.query.utm_content,
  roomOption: state.roomOption,
  isLoggedIn: selectLoggedIn(appState),
  isSpoofed: appState.auth.account.isSpoofed,
  userMultiItemCartEnabled: isMultiCartEnabled(appState),
  open: !!state.activeFilter,
  selectedTravellerEmployeeIds: appState.businessTraveller.selectedTravellerEmployeeIds,
  selectedTravellerGuestIds: appState.businessTraveller.selectedTravellerGuestIds,
}), PropertyRateOption, preventRenderWithOpenFilter)
